import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Divider,
  Popover,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode2,
  QrCodeScanner,
  Add as AddIcon,
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
  NavigateBefore,
  NavigateNext,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GetEmployees,
  UpdateEmployee,
  DeleteEmployee,
  GenerateEmployeeQR,
} from "../../../Services/Services";

// ✅ Reusable Truncated Cell with Tooltip
const TruncatedCell = ({ text, width, align = "center" }) => {
  const displayText = text || "-";

  return (
    <TableCell
      align={align}
      sx={{
        minWidth: width,
        maxWidth: width,
        width: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Tooltip title={displayText} arrow placement="top">
        <Box
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          {displayText}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

function Employees() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    email: "",
    mobile: "",
    dob: "",
    gender: "",
    city: "",
    address: "",
    password: "",
  });

  const [showQRModal, setShowQRModal] = useState(false);
  const [qrEmployee, setQrEmployee] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [empToDelete, setEmpToDelete] = useState(null);

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    let data = employees;
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      data = data.filter(
        (emp) =>
          (emp.full_name || "").toLowerCase().includes(s) ||
          (emp.unique_id || "").toLowerCase().includes(s) ||
          (emp.email || "").toLowerCase().includes(s) ||
          (emp.mobile || "").toLowerCase().includes(s) ||
          (emp.city || "").toLowerCase().includes(s) ||
          (emp.address || "").toLowerCase().includes(s)
      );
    }
    setFilteredEmployees(data);
    setCurrentPage(1);
  }, [search, employees]);

  const loadEmployees = async () => {
    try {
      const data = await GetEmployees();
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (err) {
      console.error("Load employees error:", err);
      toast.error(err.response?.data?.message || "Failed to load employees");
    }
  };

  const deleteEmployee = async (id) => {
    try {
      await DeleteEmployee(id);
      toast.success("Employee deleted successfully");
      await loadEmployees();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const openEditModal = (emp) => {
    setEditingEmployee(emp);
    setEditFormData({
      full_name: emp.full_name || "",
      email: emp.email || "",
      mobile: emp.mobile || "",
      dob: emp.dob || "",
      gender: emp.gender || "",
      city: emp.city || "",
      address: emp.address || "",
      password: "",
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingEmployee(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      setEditFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, "") }));
      return;
    }
    if (name === "email") {
      setEditFormData((prev) => ({ ...prev, [name]: value.toLowerCase() }));
      return;
    }
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateEmployee = async () => {
    if (editFormData.mobile && !/^[0-9]{10}$/.test(editFormData.mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (
      editFormData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)
    ) {
      toast.error("Please enter a valid email address");
      return;
    }
    // 👈 पासवर्ड लेंथ वैलिडेशन जोड़ें (यदि भरा गया हो)
    if (editFormData.password && editFormData.password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    try {
      // 👈 केवल पासवर्ड टाइप होने पर ही उसे अपडेट के लिए भेजें
      const payload = { ...editFormData };
      if (!payload.password) {
        delete payload.password; // खाली रहने पर पासवर्ड अपडेट नहीं होगा
      }

      const response = await UpdateEmployee(editingEmployee.id, payload);
      toast.success(response.message || "Employee updated successfully");
      closeEditModal();
      await loadEmployees();
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.response?.data?.message || "Update failed");
    }
  };

  const openQRModal = (emp) => {
    setQrEmployee(emp);
    setShowQRModal(true);
  };
  const closeQRModal = () => {
    setShowQRModal(false);
    setQrEmployee(null);
  };

  const generateQR = async () => {
    if (!qrEmployee) return;
    setQrLoading(true);
    try {
      const response = await GenerateEmployeeQR(qrEmployee.id);
      const updated = {
        ...qrEmployee,
        qr_token: response.qr_token,
        qr_generated_at: response.generated_at,
      };
      setQrEmployee(updated);
      setEmployees((prev) =>
        prev.map((e) => (e.id === qrEmployee.id ? updated : e))
      );
      toast.success("QR code generated successfully!");
    } catch (err) {
      console.error("QR generation error:", err);
      toast.error(err.response?.data?.message || "Failed to generate QR code");
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQR = () => {
    const canvas = document.querySelector(".qr-code-wrapper canvas");
    if (!canvas) {
      toast.error("QR code not found");
      return;
    }
    const newCanvas = document.createElement("canvas");
    const padding = 30,
      textHeight = 55;
    newCanvas.width = canvas.width + padding * 2;
    newCanvas.height = canvas.height + padding * 2 + textHeight;
    const ctx = newCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
    ctx.drawImage(canvas, padding, padding);
    ctx.fillStyle = "#1a1a2e";
    ctx.font = "bold 15px Arial";
    ctx.textAlign = "center";
    const cx = newCanvas.width / 2;
    ctx.fillText(qrEmployee.full_name || "Employee", cx, canvas.height + padding + 22);
    ctx.font = "13px Arial";
    ctx.fillStyle = "#555555";
    ctx.fillText(qrEmployee.unique_id || "", cx, canvas.height + padding + 44);
    const link = document.createElement("a");
    link.download = `QR_${qrEmployee.unique_id || qrEmployee.full_name || "employee"}.png`;
    link.href = newCanvas.toDataURL("image/png");
    link.click();
    toast.success("QR code downloaded!");
  };

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(
    startIndex,
    startIndex + itemsPerPage
  );
  const toggleCardExpand = (i) =>
    setExpandedCard(expandedCard === i ? null : i);

  // Column Width Definitions
  const COL_WIDTHS = {
    sno: 60,
    name: 150,
    empId: 130,
    email: 200,
    phone: 120,
    gender: 100,
    city: 120,
    password: 120,
    joined: 130,
    action: 130,
  };

  return (
    <>
      <ToastContainer />
      <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
        {/* FILTERS + ADD BUTTON */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Employees"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: search && (
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/dashboard/add-employee")}
                sx={{ height: 32, fontSize: "0.7rem", borderRadius: 0 }}
              >
                Employee
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* MOBILE VIEW */}
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper" }}>
            {paginatedEmployees.map((emp, index) => {
              const globalIndex = startIndex + index;
              return (
                <Box key={emp.id}>
                  <Box
                    onClick={() => toggleCardExpand(globalIndex)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: globalIndex % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:active": { bgcolor: "#e3f2fd" },
                      borderLeft: "4px solid #1976d2",
                    }}
                  >
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
                        >
                          #{globalIndex + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {emp.full_name || "-"}
                        </Typography>
                        <Chip
                          label={emp.unique_id || "-"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor: "#1976d2",
                            "& .MuiChip-label": { px: 1, color: "#ffffff" },
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {emp.email?.substring(0, 25) || "-"}
                        </span>
                        <span style={{ color: "#999" }}>•</span>
                        <span style={{ fontSize: "0.7rem" }}>
                          {emp.mobile || "-"}
                        </span>
                      </Typography>
                    </Box>
                    <IconButton size="small">
                      {expandedCard === globalIndex ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse
                    in={expandedCard === globalIndex}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: globalIndex % 2 === 0 ? "#f8f9fa" : "#fafafa",
                        borderLeft: "4px solid #1976d2",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 0.8,
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            GENDER
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {emp.gender || "-"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            CITY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {emp.city || "-"}
                          </Typography>
                        </Box>
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            ADDRESS
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {emp.address || "-"}
                          </Typography>
                        </Box>
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            PASSWORD
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#1976d2", 
                              wordBreak: "break-all", 
                            }}
                          >
                            {emp.password || "-"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            JOINED
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {emp.created_at
                              ? new Date(emp.created_at).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 0.6,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(emp);
                          }}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": { bgcolor: "#bbdefb" },
                          }}
                        >
                          <EditIcon
                            sx={{ fontSize: "1rem", color: "#1976d2" }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openQRModal(emp);
                          }}
                          sx={{
                            bgcolor: "#fff8e1",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": { bgcolor: "#ffecb3" },
                          }}
                        >
                          {emp.qr_token ? (
                            <QrCode2 sx={{ fontSize: "1rem", color: "#f57f17" }} />
                          ) : (
                            <QrCodeScanner
                              sx={{ fontSize: "1rem", color: "#f57f17" }}
                            />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setEmpToDelete(emp.id);
                          }}
                          sx={{
                            bgcolor: "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": { bgcolor: "#ffcdd2" },
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "1rem", color: "#d32f2f" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Collapse>

                  <Divider />
                </Box>
              );
            })}
          </Box>
        ) : (
          /* DESKTOP TABLE VIEW */
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "88vh", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1100,
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #ccc",
                  whiteSpace: "nowrap",
                  padding: "8px 16px !important",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    color: "#333",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      left: 0,
                      background: "#f5f5f5",
                      zIndex: 10,
                      width: COL_WIDTHS.sno,
                      minWidth: COL_WIDTHS.sno,
                      maxWidth: COL_WIDTHS.sno,
                    }}
                  >
                    S.No.
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      left: COL_WIDTHS.sno,
                      background: "#f5f5f5",
                      zIndex: 10,
                      width: COL_WIDTHS.name,
                      minWidth: COL_WIDTHS.name,
                      maxWidth: COL_WIDTHS.name,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.empId,
                      minWidth: COL_WIDTHS.empId,
                      maxWidth: COL_WIDTHS.empId,
                    }}
                  >
                    Employee ID
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.email,
                      minWidth: COL_WIDTHS.email,
                      maxWidth: COL_WIDTHS.email,
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.phone,
                      minWidth: COL_WIDTHS.phone,
                      maxWidth: COL_WIDTHS.phone,
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.gender,
                      minWidth: COL_WIDTHS.gender,
                      maxWidth: COL_WIDTHS.gender,
                    }}
                  >
                    Gender
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.city,
                      minWidth: COL_WIDTHS.city,
                      maxWidth: COL_WIDTHS.city,
                    }}
                  >
                    City
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.password,
                      minWidth: COL_WIDTHS.password,
                      maxWidth: COL_WIDTHS.password,
                    }}
                  >
                    Password
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: COL_WIDTHS.joined,
                      minWidth: COL_WIDTHS.joined,
                      maxWidth: COL_WIDTHS.joined,
                    }}
                  >
                    Joined
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 10,
                      width: COL_WIDTHS.action,
                      minWidth: COL_WIDTHS.action,
                      maxWidth: COL_WIDTHS.action,
                      boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.2)",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEmployees.length > 0 ? (
                  paginatedEmployees.map((emp, index) => (
                    <TableRow
                      hover
                      key={emp.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#e3f2fd !important",
                          "& .MuiTableCell-root": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "inherit",
                          zIndex: 5,
                          width: COL_WIDTHS.sno,
                          minWidth: COL_WIDTHS.sno,
                          maxWidth: COL_WIDTHS.sno,
                        }}
                      >
                        {startIndex + index + 1}
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          left: COL_WIDTHS.sno,
                          backgroundColor: "inherit",
                          zIndex: 5,
                          width: COL_WIDTHS.name,
                          minWidth: COL_WIDTHS.name,
                          maxWidth: COL_WIDTHS.name,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={emp.full_name || "-"} arrow placement="top">
                          <Box
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {emp.full_name || "-"}
                          </Box>
                        </Tooltip>
                      </TableCell>

                      <TruncatedCell text={emp.unique_id} width={COL_WIDTHS.empId} />
                      <TruncatedCell text={emp.email} width={COL_WIDTHS.email} />
                      <TruncatedCell text={emp.mobile} width={COL_WIDTHS.phone} />
                      <TruncatedCell text={emp.gender} width={COL_WIDTHS.gender} />
                      <TruncatedCell text={emp.city} width={COL_WIDTHS.city} />
                      <TruncatedCell text={emp.password} width={COL_WIDTHS.password} />
                      <TruncatedCell
                        text={
                          emp.created_at
                            ? new Date(emp.created_at).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )
                            : "-"
                        }
                        width={COL_WIDTHS.joined}
                      />

                      <TableCell
                        align="center"
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "inherit",
                          zIndex: 5,
                          width: COL_WIDTHS.action,
                          minWidth: COL_WIDTHS.action,
                          maxWidth: COL_WIDTHS.action,
                          boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.1)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditModal(emp)}
                              sx={{ p: 0.5 }}
                            >
                              <EditIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={emp.qr_token ? "View QR" : "Generate QR"}
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => openQRModal(emp)}
                              sx={{ p: 0.5, color: "#f57f17" }}
                            >
                              {emp.qr_token ? (
                                <QrCode2 sx={{ fontSize: "1rem" }} />
                              ) : (
                                <QrCodeScanner sx={{ fontSize: "1rem" }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                setEmpToDelete(emp.id);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: "center", py: 5 }}>
                      <Typography sx={{ fontSize: "48px" }}>👤</Typography>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          color: "#1a1a2e",
                          fontWeight: 500,
                        }}
                      >
                        No employees found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              startIcon={<NavigateBefore />}
            >
              Prev
            </Button>
            <Typography
              sx={{ fontWeight: 500, color: "#333", fontSize: "0.85rem" }}
            >
              {currentPage} / {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              endIcon={<NavigateNext />}
            >
              Next
            </Button>
          </Box>
        )}

        {/* DELETE POPOVER */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setEmpToDelete(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="body2">Are you sure?</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                color="error"
                size="small"
                onClick={async () => {
                  await deleteEmployee(empToDelete);
                  setAnchorEl(null);
                  setEmpToDelete(null);
                }}
                sx={{ mr: 1 }}
              >
                Yes
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setAnchorEl(null);
                  setEmpToDelete(null);
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* EDIT MODAL */}
        <Dialog
          open={showEditModal}
          onClose={closeEditModal}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ fontWeight: "bold", pb: 0, fontSize: "1.1rem" }}>
            Edit Employee
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name *"
                  name="full_name"
                  value={editFormData.full_name}
                  onChange={handleEditChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email *"
                  name="email"
                  type="email"
                  value={editFormData.email}
                  onChange={handleEditChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mobile *"
                  name="mobile"
                  value={editFormData.mobile}
                  onChange={handleEditChange}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={editFormData.dob}
                  onChange={handleEditChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    label="Gender"
                    value={editFormData.gender}
                    onChange={handleEditChange}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  name="city"
                  value={editFormData.city}
                  onChange={handleEditChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="New Password"
                  name="password"
                  type="password"
                  placeholder="Leave blank to keep unchanged"
                  value={editFormData.password}
                  onChange={handleEditChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Address"
                  name="address"
                  multiline
                  rows={3}
                  value={editFormData.address}
                  onChange={handleEditChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeEditModal} variant="outlined">
              Cancel
            </Button>
            <Button onClick={updateEmployee} variant="contained">
              Update Employee
            </Button>
          </DialogActions>
        </Dialog>

        {/* QR MODAL */}
        <Dialog
          open={showQRModal}
          onClose={closeQRModal}
          maxWidth="sm"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ fontWeight: "bold", pb: 0, fontSize: "1.1rem" }}>
            QR Code — {qrEmployee?.full_name}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {qrEmployee && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    p: 1.5,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    justifyContent: "space-around",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}
                    >
                      NAME
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                      {qrEmployee.full_name || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}
                    >
                      ID
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: "monospace",
                      }}
                    >
                      {qrEmployee.unique_id || "-"}
                    </Typography>
                  </Box>
                </Box>

                {qrEmployee.qr_token ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      py: 2,
                    }}
                  >
                    <Box
                      className="qr-code-wrapper"
                      sx={{
                        p: 2,
                        bgcolor: "#fff",
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <QRCodeCanvas
                        value={qrEmployee.qr_token}
                        size={200}
                        level="H"
                        includeMargin={true}
                      />
                    </Box>
                    {qrEmployee.qr_generated_at && (
                      <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
                        Generated:{" "}
                        {new Date(qrEmployee.qr_generated_at).toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={downloadQR}
                      >
                        Download
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={generateQR}
                        disabled={qrLoading}
                      >
                        {qrLoading ? "Generating..." : "New QR"}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      py: 4,
                    }}
                  >
                    <Typography sx={{ fontSize: "48px" }}>📲</Typography>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                      No QR code generated yet
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                      Generate a unique QR code for this employee
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={generateQR}
                      disabled={qrLoading}
                      sx={{ mt: 1 }}
                    >
                      {qrLoading ? "Generating..." : "✨ Generate QR Code"}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeQRModal} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default Employees;